/**
 * useSignupIntent — fetch the tenant's signup context from Control Plane.
 *
 * Returns intent + qualification data. Used by dashboard onboarding
 * to auto-select the matching path and show intent-specific setup (Phase 7/9).
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { cpGetSignupContext, type SignupContextResponse } from "@/lib/api/controlPlaneSignupContext";

/**
 * Maps signup intents to dashboard onboarding path IDs.
 * - chat → chat path (open inbox)
 * - selling → selling path (add product + customer)
 * - pos → selling path (same steps — add products/items)
 * - crm → contacts path (add customer + lead)
 * - null/undefined → no auto-select (generic 3-path picker)
 */
const INTENT_TO_PATH: Record<string, string> = {
  chat: "chat",
  selling: "selling",
  pos: "selling",
  crm: "contacts",
};

export function useSignupIntent() {
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["signup-context"],
    queryFn: cpGetSignupContext,
    staleTime: Infinity, // Signup context never changes after registration
    gcTime: 30 * 60 * 1000,
    enabled: hasHydrated && isAuthenticated,
    select: (data: SignupContextResponse) => ({
      intent: data.signup_intent ?? null,
      pathId: data.signup_intent ? (INTENT_TO_PATH[data.signup_intent] ?? null) : null,
      qualification: data.signup_qualification ?? null,
      conversionValue: data.signup_conversion_value ?? null,
      onboardingCompleted: data.onboarding_completed ?? false,
    }),
  });
}
